/**
 * Course player outline sidebar.
 */
import React, { Fragment, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Collapse, Divider } from '@mui/material';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { VButton, VText } from "form";
import ToInstructor from 'pages/common/ToInsructor';
import { useAsterController } from 'context';
import { asFunc } from 'utils/safeData';

/**
 * Nested section/sequence/vertical navigation for the current course.
 *
 * @param {{ open?: boolean, setOpen?: Function }} props
 */
const BoardSidebar = (props) => {

  const { t } = useTranslation('common');

  const setMobileSidebar = asFunc(props.setMobileSidebar);

  const location = useLocation();
  const navigate = useNavigate();
  const { courseId, sectionIndex } = useParams();

  const [controller] = useAsterController();
  const { structure, viewedVerticals, loadedCourseDetail, preview } = controller;

  const [selected, setSelected] = useState({});
  const [wrap, setWrap] = useState(false);

  useEffect(() => setSelected({ [sectionIndex]: true }), [sectionIndex]);

  const renderIcon = (xblocks, sectionIdx, sequenceIdx, verticalIdx) => {
    if (viewedVerticals.includes(`${sectionIdx}/${sequenceIdx}/${verticalIdx}`)) {
      return <VButton className="w-9 h-9 p-0" iconOnly><Icon className="text-4xl color-main1" icon="akar-icons:circle-check-fill" /></VButton>;
    }

    const types = (xblocks || []).map(x => x.type);

    if (types.includes('problem')) {
      return <VButton className="w-9 h-9 p-0 border-2 border-solid border-gray-200 dark:border-gray-500 bg-white" iconOnly><Icon className="color-secondary text-xl" icon="eva:edit-outline" /></VButton>;
    } else if (types.includes('video') || types.includes('videoxblock')) {
      return <VButton className="w-9 h-9 p-0 border-2 border-solid border-gray-200 dark:border-gray-500 bg-white" iconOnly><Icon className="color-secondary text-xl" icon="fluent:video-24-regular" /></VButton>;
    } else {
      return <VButton className="w-9 h-9 p-0 border-2 border-solid border-gray-200 dark:border-gray-500 bg-white" iconOnly><Icon className="color-secondary text-xl" icon="fe:document" /></VButton>;
    }
  };

  return <Box className="flex-shrink-0 w-80 border-0 border-r border-solid transition-all border-gray-200 dark:border-gray-500 p-4 pr-3 h-full overflow-x-hidden" sx={{ maxWidth: wrap ? '65px' : '320px' }}>
    <Box className='flex justify-between items-center mb-4'>
      {!wrap && <VText className="text-xl ml-2" div>{t("Menu")}</VText>}
      <VButton iconOnly onClick={() => { setWrap(!wrap); setMobileSidebar(false); }}><Icon icon="heroicons-solid:menu-alt-1" className='text-xl dark:text-gray-500' /></VButton>
    </Box>

    <Box className="w-72">
      {
        loadedCourseDetail ?
          structure.sections.map((section, section_idx) => <Box key={section.section_id}>
            <VButton color="body" variant="text" className="w-full justify-between text-lg h-16" onClick={() => setSelected({ ...selected, [section_idx]: !selected[section_idx] })}>
              <Box className="font-bold text-left">{wrap ? section.section_title.toLowerCase().includes('module bonus') ? section.section_title.slice(0, 2) : section.section_title.slice(0, 1) : section.section_title}</Box>
              {selected[section_idx] ? <Icon icon="bi:chevron-up" className='flex-shrink-0' /> : <Icon icon="bi:chevron-down" className='flex-shrink-0' />}
            </VButton>
            <Collapse in={selected[section_idx]} timeout="auto">
              {
                section.sequences.map((sequence, sequence_idx) => <Fragment key={sequence.sequence_id}>
                  <VText color="secondary" className="text-base ml-2 leading-5 mb-4 mt-2 text-limit-1" div>
                    {wrap ? sequence.sequence_title.toLowerCase().includes('module bonus') ? sequence.sequence_title.slice(0, 2) : sequence.sequence_title.slice(0, 2) : sequence.sequence_title}
                  </VText>

                  {
                    sequence.verticals.map((vertical, vertical_idx) => {
                      const unitPath = `/${preview ? 'preview' : 'content'}/${courseId}/${section_idx}/${sequence_idx}/${vertical_idx}`;
                      return (
                      <Box className="flex" key={vertical.vertical_id}>
                        <Box
                          className="w-9 h-9 flex items-center justify-center z-10 cursor-pointer"
                          onClick={() => {
                            setMobileSidebar(false);
                            navigate(unitPath);
                          }}
                        >
                          {renderIcon(vertical.xblock_ids, section_idx, sequence_idx, vertical_idx)}
                        </Box>
                        <Box className="flex-1 break-all mx-2 mb-2 relative z-0 pl-1">
                          <Link to={unitPath} className="no-underline">
                            <VButton onClick={() => setMobileSidebar(false)} disableElevation
                              className={`flex-col items-start px-2 w-full ${location.pathname.includes(unitPath) ? "bg-gray-200 dark:bg-gray-700" : ""}`}
                            >
                              <VText className="text-sm text-limit-1">{vertical.vertical_title}</VText>
                              <VText color="secondary" className="text-sm mt-1" div>{vertical.duration} min</VText>
                            </VButton>
                          </Link>

                          {vertical_idx !== sequence.verticals?.length - 1 && <Box className={`w-0.5 h-4/5 absolute -left-[27px] top-[34px] ${viewedVerticals.includes(`${section_idx}/${sequence_idx}/${vertical_idx}`) ? 'bg-main1' : 'bg-gray-200'}`} />}
                        </Box>
                      </Box>
                      );
                    })
                  }
                </Fragment>)
              }
            </Collapse>
            <Divider className={`hidden md:flex border-gray-200 dark:border-gray-500 ${wrap ? 'w-8' : 'w-full'}`} />
          </Box>)
          :
          [...Array(8).keys()].map(v => <Box key={v} color="body" variant="text" className="w-full flex justify-between text-lg h-16">
            <Skeleton className="w-48" />
            <Icon icon="bi:chevron-down" />
          </Box>)
      }

      <ToInstructor />
    </Box>

  </Box >;
};

BoardSidebar.propTypes = {
  setMobileSidebar: PropTypes.func,
};

export default BoardSidebar;